import { useEffect, useState } from "react";
import type { ChannelConnection, ConversationListItem, Product } from "@qbanq/shared";
import { api } from "../api/client";

interface WebhookHealthRow {
  channel: string;
  last_received_at?: number;
  last_processed_at?: number;
  recent_errors: number;
  recent_total: number;
}

export function DashboardPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [channels, setChannels] = useState<ChannelConnection[]>([]);
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [health, setHealth] = useState<WebhookHealthRow[]>([]);
  const [agentHealth, setAgentHealth] = useState<{
    ai_failure_handoffs_24h: number;
    last_failure_at: number | null;
  } | null>(null);
  const [usage, setUsage] = useState<{ totals?: { input_tokens?: number; output_tokens?: number; invocations?: number } } | null>(null);
  const [dlqCount, setDlqCount] = useState<number>(0);

  useEffect(() => {
    // Channels/audit endpoints are admin-only; tolerate 403 for agent users.
    const safe = <T,>(p: Promise<T>, fallback: T) => p.catch(() => fallback);
    void Promise.all([
      safe(api.get<Product[]>("/api/products"), []),
      safe(api.get<ChannelConnection[]>("/api/channels"), []),
      safe(api.get<ConversationListItem[]>("/api/inbox/conversations?limit=100"), []),
      safe(api.get<WebhookHealthRow[]>("/api/audit/webhook-health"), []),
      safe(
        api.get<{ ai_failure_handoffs_24h: number; last_failure_at: number | null }>("/api/audit/agent-health"),
        null,
      ),
      safe(api.get<{ totals?: { input_tokens?: number; output_tokens?: number; invocations?: number } }>("/api/audit/agent-usage"), null),
      safe(api.get<unknown[]>("/api/audit/dlq"), []),
    ]).then(([p, c, conv, h, ah, u, dlq]) => {
      setProducts(p);
      setChannels(c);
      setConversations(conv);
      setHealth(h);
      setAgentHealth(ah);
      setUsage(u);
      setDlqCount((dlq as unknown[]).length);
    });
  }, []);

  const openHandoffs = conversations.filter((c) => c.unresolved_handoff).length;

  return (
    <div>
      <h2>Dashboard</h2>
      <div className="grid cols-3">
        <Stat label="Products" value={products.length} />
        <Stat label="Connected channels" value={channels.filter((c) => c.status === "connected").length} />
        <Stat label="Open handoffs" value={openHandoffs} />
      </div>

      <div className="grid cols-3">
        <Stat label="AI invocations (24h)" value={usage?.totals?.invocations ?? 0} />
        <Stat
          label="Tokens in/out (24h)"
          value={(usage?.totals?.input_tokens ?? 0) + (usage?.totals?.output_tokens ?? 0)}
        />
        <Stat label="Dead-letter messages" value={dlqCount} />
      </div>

      {dlqCount > 0 && (
        <div className="card" style={{ borderColor: "var(--danger)" }}>
          <strong className="error">⚠ {dlqCount} dead-letter message(s)</strong>
          <div className="muted" style={{ fontSize: 13 }}>
            Messages that exhausted retries. Inspect via <code>/api/audit/dlq</code>.
          </div>
        </div>
      )}

      {agentHealth && agentHealth.ai_failure_handoffs_24h > 0 && (
        <div className="card" style={{ borderColor: "var(--danger)" }}>
          <strong className="error">⚠ AI auto-handoffs in last 24h: {agentHealth.ai_failure_handoffs_24h}</strong>
          <div className="muted" style={{ fontSize: 13 }}>
            The AI layer failed repeatedly and conversations were escalated to humans
            {agentHealth.last_failure_at ? ` (last: ${new Date(agentHealth.last_failure_at).toLocaleString()})` : ""}.
            Check the Anthropic API status and secrets.
          </div>
        </div>
      )}

      <div className="card">
        <h3>Webhook health (24h)</h3>
        <table>
          <thead>
            <tr>
              <th>Channel</th>
              <th>Deliveries</th>
              <th>Errors</th>
              <th>Last received</th>
            </tr>
          </thead>
          <tbody>
            {health.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">No deliveries yet.</td>
              </tr>
            )}
            {health.map((h) => (
              <tr key={h.channel}>
                <td>{h.channel}</td>
                <td>{h.recent_total}</td>
                <td className={h.recent_errors > 0 ? "error" : ""}>{h.recent_errors}</td>
                <td>{h.last_received_at ? new Date(h.last_received_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="card">
      <div className="muted">{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
